import { useSelector } from 'react-redux';
import {
  NavLink as RouterLink,
  useNavigate,
  useLocation
} from 'react-router-dom';
import {
  // ListSubheader,
  alpha,
  Box,
  List,
  styled,
  Button,
  ListItem,
  Collapse
} from '@mui/material';

import { PeopleAlt } from '@mui/icons-material';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SecurityIcon from '@mui/icons-material/Security';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';

// import AccountCircleTwoToneIcon from '@mui/icons-material/AccountCircleTwoTone';
// import DisplaySettingsTwoToneIcon from '@mui/icons-material/DisplaySettingsTwoTone';
import DescriptionIcon from '@mui/icons-material/Description';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PaidIcon from '@mui/icons-material/Paid';
import { useState, useEffect } from 'react';
import { RootState } from '../../../../state/store';
import { isAdmin, isAssistant, isDoctor, isTrialUser } from '../../../../utils';

const MenuItemSpan = styled('span')({
  fontSize: '22px',
  paddingRight: '5px'
});

const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: ${theme.spacing(1)};

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: bold;
      font-size: ${theme.typography.pxToRem(12)};
      color: ${theme.colors.alpha.trueWhite[50]};
      padding: ${theme.spacing(0, 2.5)};
      line-height: 1.4;
    }

    .active-main-tab {
      background-color: ${alpha(theme.colors.alpha.trueWhite[100], 0.06)};
      color: ${theme.colors.alpha.trueWhite[100]};
    }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {

      .MuiListItem-root {
        padding: 1px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }
    
        .MuiButton-root {
          display: flex;
          color: ${theme.colors.alpha.trueWhite[70]};
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 3)};

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            color: ${theme.colors.alpha.trueWhite[30]};
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
          }
          
          .MuiButton-endIcon {
            color: ${theme.colors.alpha.trueWhite[50]};
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: ${alpha(theme.colors.alpha.trueWhite[100], 0.06)};
            color: ${theme.colors.alpha.trueWhite[100]};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.colors.alpha.trueWhite[100]};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: ${theme.colors.alpha.trueWhite[100]};
                opacity: 0;
                transition: ${theme.transitions.create([
                  'transform',
                  'opacity'
                ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`
);

function SidebarMenu() {
  const [selectedMainTab, setSelectedMainTab] = useState([]);
  const {
    role,
    officeId,
    subscription: { _id: subscriptionId, hasExpired },
    practiceLocationEnabled
  } = useSelector((state: RootState) => state.user.userData);
  const navigate = useNavigate();
  const location = useLocation();
  const handleMainTabClick = (tab) => {
    // If the tab has a direct link, navigate to it.
    if (tab.link) {
      navigate(tab.link);
      // Collapse all tabs when navigating to a direct link
      setSelectedMainTab([]);
    }

    // If the tab has sub-items, toggle its expanded/collapsed state.
    if (tab.subItems && tab.subItems.length > 0) {
      if (selectedMainTab.includes(tab.mainTab)) {
        setSelectedMainTab((prev) =>
          prev.filter((currTab) => currTab !== tab.mainTab)
        );
      } else {
        // Collapse all other tabs and expand the clicked one
        setSelectedMainTab([tab.mainTab]);
      }
    } else {
      // If the clicked tab does not have sub-items, collapse all expanded tabs
      setSelectedMainTab([]);
    }
  };

  const renderSubMenu = (items) => (
    <Collapse
      in={selectedMainTab.includes(items.mainTab)}
      timeout="auto"
      unmountOnExit
    >
      <SubMenuWrapper>
        <List component="div">
          {items.subItems.map((subItem) => (
            <ListItem key={subItem.label} component="div">
              <Button
                sx={{ fontSize: '16px' }}
                disableRipple
                component={RouterLink}
                to={subItem.link}
                startIcon={subItem.icon}
              >
                {subItem.label}
                {subItem.hasWarning && (
                  <ReportProblemIcon
                    sx={{
                      color: 'orange',
                      fontSize: '16px',
                      marginLeft: '3px'
                    }}
                  />
                )}
              </Button>
            </ListItem>
          ))}
        </List>
      </SubMenuWrapper>
    </Collapse>
  );

  type MenuItem = {
    mainTab: string;
    label: string;
    subItems: any[];
    hasWarning: any;
    link?: string;
  };

  const menuItems: MenuItem[] = (({
    role,
    officeId,
    subscriptionId,
    practiceLocationEnabled,
    hasExpired
  }) => {
    const hasWarning = !isTrialUser(role)
      ? !(officeId && practiceLocationEnabled && subscriptionId && !hasExpired)
      : false;
    // Admin-only Account (roles/permissions). Doctor/assistant Account +
    // Subscriptions live in Header Userbox dropdown (UserMenuOptions).
    const accountsTab = {
      mainTab: 'accounts',
      label: 'Account',
      subItems: [
        {
          label: 'Manage Roles',
          link: '/accounts/roles',
          icon: <PeopleAlt />
        },
        {
          label: 'Manage Permissions',
          link: '/accounts/permissions',
          icon: <SecurityIcon />
        },
        {
          label: 'Assign Roles',
          link: '/accounts/assign-roles',
          icon: <DescriptionIcon />
        }
      ],
      hasWarning
    };

    const trialUsersTab = {
      mainTab: 'users',
      label: 'Manage Users',
      subItems: [
        { label: 'Prod Users', link: '/users/all', icon: <ListAltIcon /> },
        {
          label: 'Trial Users List',
          link: '/users/trial/all',
          icon: <ListAltIcon />
        },
        {
          label: 'Create Trial User',
          link: '/users/trial/create',
          icon: <PersonAddAltIcon />
        }
      ],
      hasWarning: null
    };

    const dashboardTab = {
      mainTab: 'dashboard',
      label: 'Dashboard',
      subItems: [
        {
          label: 'Dashboard',
          link: '/dashboard',
          icon: <DashboardIcon />
        },
        {
          label: 'AI Costing',
          link: '/ai-costing',
          icon: <PaidIcon />
        }
      ],
      hasWarning: null
    };

    const reportTab = {
      mainTab: 'reports',
      label: 'Reports',
      subItems: [
        {
          label: 'All Reports',
          link: '/reports/all',
          icon: <DescriptionIcon />
        }
      ],
      hasWarning: null
    };

    const canShowReportsTab =
      (isDoctor(role) || isAssistant(role)) &&
      !!officeId &&
      !!practiceLocationEnabled &&
      !!subscriptionId;

    const plansTab = {
      mainTab: 'plans',
      label: 'Plans/Procedure Setup',
      subItems: [
        {
          label: 'CDT Codes',
          link: '/plans/cdt-codes',
          icon: <DescriptionIcon />
        },
        {
          label: 'Internal Codes',
          link: '/plans/internal-codes',
          icon: <DescriptionIcon />
        }
      ],
      hasWarning: null
    };

    const copilotBatchProcessorDashboardTab = {
      mainTab: 'copilot-batch-processor',
      label: 'Batch Processor',
      link: '/copilot-batch-processor',
      subItems: [],
      hasWarning: null
    };

    const adminResourcesTab = {
      mainTab: 'admin-resources',
      label: 'Resources',
      link: '/admin/resources',
      subItems: [],
      hasWarning: null
    };

    const DentScribeGPS = {
      mainTab: 'dentscribe gps',
      label: 'DentScribe GPS',
      link: '/gps',
      subItems: [],
      hasWarning: null
    };

    const teamSetupTab = {
      mainTab: 'team-setup',
      label: 'Team Setup',
      link: '/staff-contacts',
      subItems: [],
      hasWarning: null
    };

    if (
      officeId &&
      practiceLocationEnabled &&
      subscriptionId &&
      !hasExpired &&
      isAdmin(role)
    ) {
      return [
        dashboardTab,
        accountsTab,
        trialUsersTab,
        adminResourcesTab,
        copilotBatchProcessorDashboardTab
      ];
    } else if (
      // officeId && practiceLocationEnabled
      // && subscriptionId && !hasExpired &&
      isDoctor(role) ||
      isAssistant(role)
    ) {
      const tabs = [];
      if (
        subscriptionId &&
        officeId &&
        practiceLocationEnabled &&
        !hasExpired
      ) {
        tabs.push(DentScribeGPS);
      }
      return [
        ...tabs,
        ...(canShowReportsTab ? [reportTab] : []),
        plansTab,
        teamSetupTab
      ];
    }

    // Account + Subscriptions moved to Userbox dropdown for non-admin roles
    return [];
  })({ role, officeId, subscriptionId, practiceLocationEnabled, hasExpired });

  // Sync selectedMainTab with current route
  useEffect(() => {
    // Find the main tab whose subItems (if any) contain the current path
    const currentPath = location.pathname;
    const expandedTabs = menuItems
      .filter(
        (item) =>
          item.subItems && item.subItems.some((sub) => sub.link === currentPath)
      )
      .map((item) => item.mainTab);
    // Only update if different
    setSelectedMainTab((prev) => {
      // If already expanded, don't update
      if (
        expandedTabs.length === 0 ||
        (prev.length === expandedTabs.length &&
          prev.every((tab) => expandedTabs.includes(tab)))
      ) {
        return prev;
      }
      return expandedTabs;
    });
  }, [
    location.pathname,
    role,
    officeId,
    subscriptionId,
    practiceLocationEnabled,
    hasExpired
  ]);

  return (
    <>
      {practiceLocationEnabled !== null ? (
        <MenuWrapper>
          <List component="div">
            {menuItems.map((menuItem) => {
              const isSingleLinkItem =
                menuItem.link &&
                (!menuItem.subItems || menuItem.subItems.length === 0);
              const isSingleLinkActive =
                isSingleLinkItem && location.pathname.startsWith(menuItem.link);

              return (
                <Box key={menuItem.mainTab} sx={{ py: 1 }}>
                  <Box
                    sx={{
                      pl: 2,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        cursor: 'pointer'
                      },
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    className={isSingleLinkActive ? 'active-main-tab' : ''}
                    component="div"
                    onClick={() => handleMainTabClick(menuItem)}
                  >
                    <MenuItemSpan>{menuItem.label.toUpperCase()}</MenuItemSpan>
                    {menuItem.hasWarning && (
                      <ReportProblemIcon
                        sx={{
                          color: 'orange',
                          fontSize: '18px',
                          marginBottom: '2px'
                        }}
                      />
                    )}
                  </Box>
                  {renderSubMenu(menuItem)}
                </Box>
              );
            })}
          </List>
        </MenuWrapper>
      ) : null}
    </>
  );
}

export default SidebarMenu;
